// src/components/dashboard/CustomerSummary.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, Box, Typography, TextField, CircularProgress } from '@mui/material';
import { Download } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import axios from 'axios';
import { API_GET_ENQUIRIES, API_GET_EXPORT_ENQUIRIES } from '../../Util/URLConstants';
import { downloadBlob } from '../../Util/ApiHelper';
import { ACCESS_RESTRICTED_MESSAGE, SESSION_USER_ROLES_PERMISSIONS, STATUS_CONFIRMED, STATUS_LEAD, STATUS_NOT_INTERESTED } from '../../Util/VMConstants';
import SubscriptionDialog from '../../Util/SubscriptionDialog';


// Chart.js imports for Pie
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import MUITypography from '../../Components/MUITypography';
import { align_row, vmPageTitleSx, vmStatValueSx } from '../../Styles/VMStyles';
import { useSelector } from 'react-redux';
import { checkIsPermissionExist } from '../../Util/UtilHelper';
import StorageFactory from '../../Util/StorageFactory';

// register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, Title);

const CustomerSummary = ({ defaultFrom, defaultTo, userID, canAccessPremiumFeatures }) => {
  const [fromDate, setFromDate] = useState(defaultFrom || '');
  const [toDate, setToDate] = useState(defaultTo || '');
  const [loading, setLoading] = useState(false);
  const [exportLoadingKey, setExportLoadingKey] = useState(null);
  const [data, setData] = useState({ total: 0, leads: 0, notInterested: 0, confirmed: 0 });
  const [openSubscribeDialog, setOpenSubscribeDialog] = useState(false);
  const { permissions, user } = useSelector(
    (state) => state.subscription
  );

  const sessionFactory = useMemo(
    () => new StorageFactory("session"),
    []
  );

  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);
  const isDownloadRecordPermission = checkIsPermissionExist(
    VM_PERMISSIONS,
    "VM051"
  );

  useEffect(() => {
    if (defaultFrom) setFromDate(defaultFrom);
    if (defaultTo) setToDate(defaultTo);
  }, [defaultFrom, defaultTo]);

  const fetchCounts = async () => {
    try {
      setLoading(true);
      const paramsBase = { from: fromDate || undefined, to: toDate || undefined };

      const [leadsRes, notInterestRes, confirmedRes, allRes] = await Promise.all([
        axios.get(API_GET_ENQUIRIES, { params: { ...paramsBase, user_id:userID, appointment_status: STATUS_LEAD } }),
        axios.get(API_GET_ENQUIRIES, { params: { ...paramsBase, user_id:userID,  appointment_status: STATUS_NOT_INTERESTED } }),
        axios.get(API_GET_ENQUIRIES, { params: { ...paramsBase,  user_id:userID, appointment_status: STATUS_CONFIRMED } }),
        axios.get(API_GET_ENQUIRIES, { params: { ...paramsBase , user_id:userID, } })
      ]);

      const leads = (leadsRes.data && Array.isArray(leadsRes.data.data)) ? leadsRes.data.data.length
                   : (Array.isArray(leadsRes.data) ? leadsRes.data.length : 0);
      const notInt = (notInterestRes.data && Array.isArray(notInterestRes.data.data)) ? notInterestRes.data.data.length
                   : (Array.isArray(notInterestRes.data) ? notInterestRes.data.length : 0);
      const confirmed = (confirmedRes.data && Array.isArray(confirmedRes.data.data)) ? confirmedRes.data.data.length
                   : (Array.isArray(confirmedRes.data) ? confirmedRes.data.length : 0);
      const total = (allRes.data && Array.isArray(allRes.data.data)) ? allRes.data.data.length
                   : (Array.isArray(allRes.data) ? allRes.data.length : (leads + notInt + confirmed));

      setData({ total, leads, notInterested: notInt, confirmed });
    } catch (err) {
      console.error('CustomerSummary fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, userID]);

  const handleExport = async (rowKey) => {
    if(isDownloadRecordPermission === true){
        if (!canAccessPremiumFeatures) {
          setOpenSubscribeDialog(true);
          return;
        }
        setExportLoadingKey(rowKey);
        try {
          const res = await axios.get(API_GET_EXPORT_ENQUIRIES, {
            params: { from: fromDate || undefined, to: toDate || undefined, user_id: userID },
            responseType: 'blob'
          });
          downloadBlob(res.data, `enquiries_${Date.now()}.xlsx`);
        } catch (err) {
          console.error('Export error:', err);
        } finally {
          setExportLoadingKey(null);
        }
    }else{
      alert(ACCESS_RESTRICTED_MESSAGE);
    }
  };

  // Chart data & options for Pie
  const chartData = useMemo(() => {
    return {
      labels: ['Leads', 'Not Interested', 'Confirmed'],
      datasets: [
        {
          label: 'Count',
          data: [data.leads, data.notInterested, data.confirmed],
          backgroundColor: [
            'rgba(33, 150, 243, 0.85)',
            'rgba(244, 67, 54, 0.85)',
            'rgba(76, 175, 80, 0.85)'
          ],
          borderColor: [
            'rgba(255,255,255,0.6)',
            'rgba(255,255,255,0.6)',
            'rgba(255,255,255,0.6)'
          ],
          borderWidth: 2,
        }
      ]
    };
  }, [data.leads, data.notInterested, data.confirmed]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 16,
          usePointStyle: true,
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed || 0;
            const dataset = context.dataset?.data || [];
            const total = dataset.reduce((acc, v) => acc + (Number(v) || 0), 0);
            const percent = total ? ((value / total) * 100).toFixed(1) : '0.0';
            const label = context.label || context.chart.data.labels[context.dataIndex] || '';
            return `${label}: ${value.toLocaleString()} (${percent}%)`;
          }
        }
      },
      title: {
        display: false
      }
    },
    animation: { duration: 400 }
  }), []);

  return (
    <Card
      sx={{
        boxShadow: 1,
        position: 'relative',
        border: '1px solid #E0E0E0',
        borderRadius: 2,
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            zIndex: 9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <CardContent
        sx={{
          p: 3,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            gap: 2,
          }}
        >
          <MUITypography variant="pageTitle" sx={vmPageTitleSx}>
            Customer Summary
          </MUITypography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField label="From" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            <TextField label="To" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch',
            gap: 3,
            minHeight: 320,
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2, p: 2, mb: 3, ...align_row }}>
              <Typography variant="body2" sx={{ fontSize: '16px', opacity: 0.9, mb: 0.5 }}>Total Enquired Customers</Typography>
              <Typography variant="pageTitle" sx={{ ...vmPageTitleSx, ml: 2 }}>{(data.total || 0).toLocaleString()}</Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                width: '100%',
                minHeight: 220,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ width: 220, height: 220 }}>
                <Pie data={chartData} options={chartOptions} />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              minWidth: 0,
            }}
          >
            {[
              { label: 'Leads', value: data.leads, bg: '#FFF3E0' },
              { label: 'Not Interested', value: data.notInterested, bg: '#FFEBEE' },
              { label: 'Confirmed', value: data.confirmed, bg: '#E8F5E9' }
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  flex: 1,
                  bgcolor: item.bg,
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: 72,
                }}
              >
                <Box>
                  <MUITypography variant="caption" color="text.secondary">{item.label}:</MUITypography>
                  <MUITypography variant="statValue" sx={vmStatValueSx}>{(item.value || 0).toLocaleString()}</MUITypography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleExport(item.label)}
                  disabled={exportLoadingKey === item.label}
                >
                  {exportLoadingKey === item.label ? <CircularProgress size={18} /> : <Download />}
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>

      <SubscriptionDialog
        open={openSubscribeDialog}
        onClose={() => setOpenSubscribeDialog(false)}
        onSubscribe={() => {
          setOpenSubscribeDialog(false);
          window.location.href = '/subscription';
        }}
      />
    </Card>
  );
};

export default CustomerSummary;
